import React from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * 全局错误边界组件
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // 错误上报
    this.reportError(error, errorInfo);
  }

  // 错误上报方法
  reportError = (error, errorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      retryCount: this.state.retryCount
    };

    // 发送到分析服务
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: true,
        custom_map: {
          custom_error_data: JSON.stringify(errorData)
        }
      });
    }

    // 控制台输出详细错误信息
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error Data:', errorData);
    console.groupEnd();
  };

  // 重试方法
  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  // 刷新页面
  handleRefresh = () => {
    window.location.reload();
  };

  // 返回首页
  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const { fallback: CustomFallback, showDetails = false } = this.props;

      // 如果提供了自定义错误组件
      if (CustomFallback) {
        return (
          <CustomFallback
            error={error}
            onRetry={this.handleRetry}
            onRefresh={this.handleRefresh}
            onGoHome={this.handleGoHome}
          />
        );
      }

      // 默认错误页面
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            {/* 错误图标 */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center"
            >
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
            </motion.div>

            {/* 错误标题 */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              出现了一些问题
            </h1>

            {/* 错误描述 */}
            <p className="text-gray-600 mb-6 leading-relaxed">
              很抱歉，页面遇到了意外错误。我们已经记录了这个问题，正在努力修复。
            </p>

            {/* 错误详情（开发模式） */}
            {showDetails && error && (
              <motion.details
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-6 text-left bg-gray-50 rounded-lg p-4"
              >
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                  查看错误详情
                </summary>
                <pre className="text-xs text-red-600 overflow-auto max-h-32">
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </motion.details>
            )}

            {/* 操作按钮 */}
            <div className="space-y-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={this.handleRetry}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <ArrowPathIcon className="w-4 h-4" />
                重试
              </motion.button>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={this.handleRefresh}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  刷新页面
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={this.handleGoHome}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  返回首页
                </motion.button>
              </div>
            </div>

            {/* 重试次数提示 */}
            {this.state.retryCount > 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs text-gray-500 mt-4"
              >
                已重试 {this.state.retryCount} 次
              </motion.p>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * 轻量级错误边界组件（用于小组件）
 */
export const LightErrorBoundary = ({ children, fallback }) => {
  return (
    <ErrorBoundary
      fallback={fallback || (({ onRetry }) => (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
          <p className="text-red-600 text-sm mb-2">组件加载失败</p>
          <button
            onClick={onRetry}
            className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded transition-colors"
          >
            重试
          </button>
        </div>
      ))}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
